import { RecordStrategy } from "../interfaces/record-strategy.interface";
import { ToastrService } from 'ngx-toastr';
import videojs from 'video.js';
import Record from 'videojs-record/dist/videojs.record.js';
import { Injectable, signal } from "@angular/core";
import { DomSanitizer, SafeUrl } from "@angular/platform-browser";
import { RecordMediaService } from "../services/record-media.service";
import { ChatMessageType, MediaRecordStatus } from "../../common/models";
import { BaseRecordStrategy } from "./base-record-strategy";

@Injectable()
export class WebRecordStrategy extends BaseRecordStrategy implements RecordStrategy {
  private mediaElement: HTMLMediaElement;
  private type: ChatMessageType;

  private player = null;
  private plugin = Record;
  private readonly maxDuration = 300;

  deviceReady = signal(false);
  timestamp = signal(0);
  status = signal<MediaRecordStatus>(MediaRecordStatus.INIT);

  constructor(sanitizer: DomSanitizer, private readonly recordMediaService: RecordMediaService, private readonly toastr: ToastrService) {
    super(sanitizer);
    this.player = null;
    this.plugin = Record;
  }

  async init(mediaElement: HTMLMediaElement, type: ChatMessageType): Promise<void> {
    if (!mediaElement || !type) {
      return;
    }

    this.mediaElement = mediaElement;
    this.type = type;

    return new Promise<void>((resolve, reject) => {
      const config = this.recordMediaService.getVideoJSConfigBasedOnType(this.type, this.maxDuration);
      this.player = videojs(this.mediaElement, config, () => {
        setTimeout(() => {
          this.player.deviceButton.handleClick();
        }, 50)
      });

      this.player.on('deviceReady', () => {
        this.deviceReady.set(true);
        resolve();
      });

      this.player.on('startRecord', () => {
        this.status.set(MediaRecordStatus.RECORDING);
      });

      this.player.on('finishRecord', () => {
        this.status.set(MediaRecordStatus.FINISHED);
        this.setRecordedBlob(this.player.recordedData, this.type === ChatMessageType.AUDIO_MESSAGE);
      });

      this.player.on('error', (_, error) => {
        this.toastr.error(error);
      });

      this.player.on('deviceError', () => {
        const error = this.player.deviceErrorCode || 'COMMON.browser_not_support_recording';
        this.toastr.error(error);
      });

      this.player.on('timestamp', () => {
        this.timestamp.set(this.player.record().getDuration());
      });
    });
  }

  async start(): Promise<void> {
    if (!this.player) await this.init(this.mediaElement, this.type);

    this.player.record().start();
  }

  async stop(): Promise<void> {
    await this.player.record().stop();
  }

  async pause(): Promise<void> {
    this.player.record().pause();
  }

  async resume(): Promise<void> {
    this.player.record().resume();
  }

  async reset(): Promise<void> {
    if (!this.player) {
      return;
    }

    this.player.record().reset();
    this.status.set(MediaRecordStatus.INIT);
    this.timestamp.set(0);
    this.resetRecordedData();
  }

  async destroy(): Promise<void> {
    if (!this.player) {
      return;
    }

    await this.player.dispose();
    this.player = null;
  }
}
